import { UserOutlined, LockOutlined, AppstoreOutlined, DatabaseOutlined } from '@ant-design/icons';
import {
  Form,
  Input,
  Button,
  Typography,
  Card,
  Alert,
  Space,
  Select,
  Spin,
  Checkbox,
  message
} from 'antd';
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';

import { useAuth } from '../../contexts/Auth';
import { DatabaseDto } from '../../services/databaseService';
import { getCurrentUserDatabases } from '../../services/userDatabaseService';

const { Title } = Typography;

interface LoginFormValues {
  username: string;
  password: string;
  // Email artık opsiyonel, sadece şifre sıfırlama vb. için kullanılacak
  email?: string;
  rememberMe?: boolean;
}

interface DatabaseSelectFormValues {
  databaseId: string;
}

const Login: React.FC = () => {
  const { login, loading, error: authError } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'login' | 'selectDatabase'>('login');
  const [userDatabases, setUserDatabases] = useState<any[]>([]);
  const [loadingDatabases, setLoadingDatabases] = useState<boolean>(false);
  const [selectedDatabaseId, setSelectedDatabaseId] = useState<string>('');

  // Kullanıcının yetkili olduğu veritabanlarını getir
  const fetchUserDatabases = async () => {
    setLoadingDatabases(true);
    setError(null);
    try {
      const databases = await getCurrentUserDatabases();
      if (databases && databases.length > 0) {
        setUserDatabases(databases);
        
        // Eğer sadece bir veritabanı varsa otomatik seç ve girişe devam et
        if (databases.length === 1) {
          const onlyDb = databases[0];
          setSelectedDatabaseId(onlyDb.databaseId);
          // LocalStorage'a yaz ve otomatik yönlendir
          localStorage.setItem('selectedDatabaseId', onlyDb.databaseId);
          if (onlyDb.databaseName) localStorage.setItem('selectedDatabaseName', onlyDb.databaseName);
          if (onlyDb.companyName) localStorage.setItem('selectedCompanyName', onlyDb.companyName);
          localStorage.setItem('loginTime', Date.now().toString());
          message.success('Tek veritabanı bulundu, otomatik seçim yapıldı.');
          navigate('/');
          return;
        }
      } else {
        setError('Erişim yetkiniz olan veritabanı bulunamadı.');
        // Token'ları temizle ve login sayfasına dön
        localStorage.removeItem('accessToken');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        setStep('login');
      }
    } catch (err: any) {
      console.error('Veritabanları yüklenirken hata:', err);
      setError('Veritabanları yüklenirken bir hata oluştu.');
    } finally {
      setLoadingDatabases(false);
    }
  };

  const handleSubmit = async (values: LoginFormValues) => {
    setError(null);
    
    // Giriş bilgilerini doğrula
    if (!values.username || !values.password) {
      setError('Kullanıcı adı ve şifre alanları zorunludur.');
      return;
    }
    
    // Kullanıcı adı formatını doğrula
    if (values.username.length < 3) {
      setError('Kullanıcı adı en az 3 karakter olmalıdır.');
      return;
    }
    
    // Şifre uzunluğunu kontrol et
    if (values.password.length < 6) {
      setError('Şifre en az 6 karakter olmalıdır.');
      return;
    }
    
    try {
      console.log('Login attempt with username:', values.username);
      
      // Giriş işlemini gerçekleştir
      await login({ username: values.username, password: values.password, rememberMe: values.rememberMe });
      
      // Giriş başarılıysa veritabanı seçim adımına geç
      console.log('Login successful, fetching user databases');
      setStep('selectDatabase');
      fetchUserDatabases();
    } catch (err: any) {
      console.log('Login error:', err);
      
      // Hata mesajını doğrudan göster
      if (err instanceof Error) {
        setError(err.message);
      } else if (err.response?.status === 400) {
        setError('Geçersiz kullanıcı adı veya şifre.');
      } else if (err.response?.status === 401) {
        setError('Giriş bilgileriniz yanlış. Lütfen tekrar deneyiniz.');
      } else if (err.response?.status === 404) {
        setError('API endpoint bulunamadı. Sistem yöneticisiyle iletişime geçiniz.');
      } else if (err.response?.status === 500) {
        setError('Sunucu hatası. Lütfen daha sonra tekrar deneyiniz veya sistem yöneticisiyle iletişime geçiniz.');
      } else if (err.code === 'ERR_NETWORK') {
        setError('Sunucuya bağlanılamadı. İnternet bağlantınızı kontrol ediniz.');
      } else {
        setError('Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyiniz.');
      }
    }
  };
  
  // Veritabanı seçimi işlemi
  const handleDatabaseSelect = async (databaseId: string) => {
    if (!databaseId) {
      message.error('Lütfen bir veritabanı seçiniz.');
      return;
    }
    
    try {
      // Seçilen veritabanı bilgisini localStorage'a kaydet
      localStorage.setItem('selectedDatabaseId', databaseId);
      
      // Seçilen veritabanının adını bul ve kaydet
      const selectedDb = userDatabases.find(db => db.databaseId === databaseId);
      if (selectedDb) {
        localStorage.setItem('selectedDatabaseName', selectedDb.databaseName);
        localStorage.setItem('selectedCompanyName', selectedDb.companyName);
      }
      
      // Giriş zamanını kaydet (milisaniye cinsinden timestamp)
      localStorage.setItem('loginTime', Date.now().toString());
      
      // Ana sayfaya yönlendir
      navigate('/');
    } catch (error) {
      console.error('Veritabanı seçimi sırasında hata:', error);
      message.error('Veritabanı seçimi sırasında bir hata oluştu.');
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      background: '#f0f2f5' 
    }}>
      <Card 
        style={{ 
          width: 400, 
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          borderRadius: '8px'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ fontSize: '48px', color: '#1890ff', marginBottom: '1rem' }}>
            <AppstoreOutlined />
          </div>
          <Title level={3}>
            {step === 'login' ? 'ERP Sistemi Giriş' : 'Veritabanı Seçimi'}
          </Title>
        </div>

        {(error || authError) && (
          <Alert
            message={step === 'login' ? "Giriş Hatası" : "Veritabanı Hatası"}
            description={error || authError}
            type="error"
            showIcon
            style={{ marginBottom: '1rem' }}
          />
        )}

        {step === 'login' ? (
          <Form
            name="login"
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            layout="vertical"
          >
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Lütfen kullanıcı adınızı giriniz!' }]}
              help="E-posta adresi değil, kullanıcı adınızı giriniz"
            >
              <Input 
                prefix={<UserOutlined />} 
                placeholder="Kullanıcı Adı (email değil)" 
                size="large"
                autoComplete="username"
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Lütfen şifrenizi giriniz!' }]}
            >
              <Input.Password 
                prefix={<LockOutlined />} 
                placeholder="Şifre" 
                size="large"
                autoComplete="current-password"
              />
            </Form.Item>
            
            <Form.Item name="rememberMe" valuePropName="checked">
              <Checkbox>Beni hatırla</Checkbox>
            </Form.Item>

            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                style={{ width: '100%' }}
                size="large"
                loading={loading}
              >
                Giriş Yap
              </Button>
            </Form.Item>
            
            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Link to="/forgot-password">Şifremi Unuttum</Link>
            </Space>
          </Form>
        ) : (
          <div>
            {loadingDatabases ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="large" />
                <p style={{ marginTop: '10px' }}>Veritabanları yükleniyor...</p>
              </div>
            ) : (
              <Form
                name="databaseSelect"
                layout="vertical"
                onFinish={() => handleDatabaseSelect(selectedDatabaseId)}
              >
                <Form.Item
                  label="Veritabanı Seçiniz"
                  name="databaseId"
                  rules={[{ required: true, message: 'Lütfen bir veritabanı seçiniz!' }]}
                >
                  <Select
                    placeholder="Veritabanı seçiniz"
                    onChange={(value) => setSelectedDatabaseId(value)}
                    value={selectedDatabaseId}
                    size="large"
                    style={{ width: '100%' }}
                    optionLabelProp="label"
                  >
                    {userDatabases.map(db => (
                      <Select.Option 
                        key={db.databaseId} 
                        value={db.databaseId}
                        label={db.databaseName}
                      >
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <DatabaseOutlined style={{ marginRight: 8 }} />
                          <span>{db.databaseName}</span>
                          <span style={{ color: '#8c8c8c', marginLeft: 8 }}>({db.companyName})</span>
                        </div>
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>

                <Form.Item>
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    style={{ width: '100%' }}
                    size="large"
                    disabled={!selectedDatabaseId}
                  >
                    Devam Et
                  </Button>
                </Form.Item>
                
                <Form.Item>
                  <Button 
                    type="link" 
                    style={{ width: '100%' }}
                    onClick={() => {
                      // Token'ları temizle ve login sayfasına dön
                      localStorage.removeItem('accessToken');
                      localStorage.removeItem('token');
                      sessionStorage.removeItem('token');
                      setStep('login');
                    }}
                  >
                    Farklı Hesap ile Giriş Yap
                  </Button>
                </Form.Item>
              </Form>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

export default Login;